import { useState } from "react";
import { Box } from "@mui/material";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type { RioConnectionTestResponse } from "@thalamos/aspire-common";

import {
  Banner,
  BannerList,
  Button,
  HelperText,
} from "~/components/design-system/index.js";

type Outcome = RioConnectionTestResponse | { kind: "could_not_run" };

/**
 * Asks Rio to authenticate with an instance's saved configuration and shows
 * the outcome. Unsaved edits are not what Rio would see, so the test waits
 * until they are saved, and an earlier outcome is hidden while they exist.
 */
export function RioConnectionTest({
  testConnection,
  hasUnsavedChanges,
}: {
  /** Rejects when the test itself could not run. */
  testConnection: () => Promisable<RioConnectionTestResponse>;
  hasUnsavedChanges: boolean;
}) {
  const [testing, setTesting] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const runTest = async () => {
    setTesting(true);
    setOutcome(null);
    try {
      setOutcome(await testConnection());
    } catch {
      setOutcome({ kind: "could_not_run" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Button
        label={testing ? "Testing connection" : "Test connection"}
        onClick={() => void runTest()}
        disabled={testing || hasUnsavedChanges}
      />
      {hasUnsavedChanges && (
        <HelperText subtext="Save your changes to test the connection with them" />
      )}
      {outcome &&
        !hasUnsavedChanges &&
        match(outcome)
          .with({ kind: "connected" }, () => (
            <Banner
              bannerType={BannerList.CONFIRMATION}
              title="Connected to Rio"
              body="Rio accepted the saved configuration."
            />
          ))
          .with({ kind: "failed" }, ({ reason }) => (
            <Banner
              bannerType={BannerList.ERROR}
              title="Could not connect to Rio"
              body={reason}
            />
          ))
          .with({ kind: "could_not_run" }, () => (
            <Banner
              bannerType={BannerList.WARNING}
              title="The connection test could not run"
              body="Try again."
            />
          ))
          .exhaustive()}
    </Box>
  );
}
